import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { Sidebar, MobileHeader } from '@widgets/sidebar';
import { setTokenProvider } from '@shared/api';
import { FeatureRequestFab } from '@features/manage-feature-requests';

/**
 * Auth-Guard-Route für Admin und Team-Mitglieder.
 * Leitet nicht eingeloggte Nutzer zu /sign-in weiter.
 * Zeigt "Zugriff verweigert" für Nutzer ohne admin/team Rolle.
 * Rendert das Dashboard-Layout mit Sidebar im Ultra-Stil für authentifizierte Nutzer.
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: () => {
    // Note: Full server-side redirect requires Clerk server-side session check.
    // Client-side guard is handled in the component below.
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      void navigate({ to: '/sign-in' });
      return;
    }
    // Token-Provider registrieren für apiFetch
    setTokenProvider(getToken);
    void getToken().then(() => {
      setAuthReady(true);
    });
  }, [isLoaded, isSignedIn, navigate, getToken]);

  if (!isLoaded || !isSignedIn || !authReady) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: 'var(--color-paper)' }}
      >
        <div className="flex flex-col items-center gap-3">
          <div
            aria-hidden="true"
            style={{
              width: '2rem',
              height: '2rem',
              border: '2px solid rgba(240,238,235,0.08)',
              borderTopColor: 'var(--color-accent)',
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
            }}
          />
          <p
            className="text-xs font-black uppercase tracking-widest"
            style={{ color: 'var(--color-ink-muted)' }}
          >
            Laden...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const role = user?.publicMetadata?.role as string | undefined;
  if (role !== 'admin' && role !== 'team') {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-screen gap-4 px-4"
        style={{ background: 'var(--color-paper)' }}
      >
        <div
          aria-hidden="true"
          style={{
            width: '3px',
            height: '2rem',
            background: 'var(--color-accent)',
            marginBottom: '0.5rem',
          }}
        />
        <h1
          className="text-2xl font-black uppercase tracking-widest"
          style={{ color: 'var(--color-ink)' }}
        >
          Zugriff verweigert
        </h1>
        <p
          className="text-sm text-center"
          style={{ color: 'var(--color-ink-muted)', maxWidth: '320px' }}
        >
          Sie haben keine Berechtigung, diesen Bereich zu nutzen.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full" style={{ background: 'var(--color-paper)' }}>
      <Sidebar />
      <div className="flex flex-1 flex-col lg:ml-60">
        <MobileHeader onMenuToggle={() => setSidebarOpen((v) => !v)} />
        {/* Suppress unused variable warning — sidebarOpen is passed to sidebar via context in future */}
        {sidebarOpen && null}
        <main className="flex-1 p-6 lg:p-8 overflow-y-auto">
          <Outlet />
        </main>
        <FeatureRequestFab />
        <footer
          className="px-6 py-3 font-mono"
          style={{
            fontSize: '0.625rem',
            color: 'rgba(138,136,128,0.4)',
            borderTop: '1px solid rgba(240,238,235,0.04)',
          }}
        >
          Build {__COMMIT_HASH__} · {__BUILD_TIME__}
        </footer>
      </div>
    </div>
  );
}
